/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// ─── Aggregator Operations Module — Entity Types ───────────────────────────────────

// Brand is now a global domain entity — re-export for backward compatibility
export type { Brand } from '../../domain/entities';

export enum CampaignStatus {
  DRAFT   = 'DRAFT',
  PLANNED = 'PLANNED',
  ACTIVE  = 'ACTIVE',
  CLOSED  = 'CLOSED',
}

export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FLAT       = 'FLAT',
}

export enum FundingType {
  NONE         = 'NONE',
  CO_FUNDED    = 'CO_FUNDED',
  FULLY_FUNDED = 'FULLY_FUNDED',
}

export interface Campaign {
  id: string;
  name: string;
  status: CampaignStatus;
  description?: string;
  // Pricing
  discountType: DiscountType;
  discountValue: number;
  fundingType: FundingType;
  /** Co-Funding percentage split (only meaningful when fundingType === CO_FUNDED) */
  sterlingSharePct?: number;
  partnerSharePct?: number;
  // Targeting
  targetAllBrands: boolean;
  brandIds: string[];
  aggregators: string[];
  // Period (Unix ms)
  validFrom: number;
  validTo: number;
  /** Exact start time (HH:mm) — used with PLANNED status for auto-post scheduling */
  startTime?: string;
  /** Admin-defined requirements shown to store managers on Submit Items page */
  importantRequirements?: string;
  /** Target number of menu items expected per brand */
  requiredItemCount?: number;
  // Meta
  createdAt: number;
  createdBy: string;
  orgId?: string;
}

export interface CampaignSettings {
  brands: import('../../domain/entities').Brand[];
  /** Admin-configurable aggregator list (replaces hardcoded DEFAULT_AGGREGATORS) */
  aggregators: string[];
  /** Base64 data-URI logos keyed by aggregator name */
  aggregatorLogos?: Record<string, string>;
  /** When false, managers can only add items via Menu Import (no manual entry). Default: true */
  allowManualEntry?: boolean;
  orgId?: string;
}

export const DEFAULT_AGGREGATORS = ['Deliveroo', 'Keeta', 'Rafeeq', 'Snoonu', 'Talabat'] as const;

// ─── Campaign Submission Workflow ──────────────────────────────────────────────

export enum SubmissionStatus {
  PENDING          = 'PENDING',
  IN_REVIEW        = 'IN_REVIEW',
  APPROVED         = 'APPROVED',
  DEPLOYMENT_READY = 'DEPLOYMENT_READY',
  POSTPONED        = 'POSTPONED',
  COMPLETED        = 'COMPLETED',
  REJECTED         = 'REJECTED',
}

/** Item lifecycle status within a submission. */
export type ItemStatus = 'ACTIVE' | 'DISCARDED';

/** Imported option group attached to a menu-linked submission item. */
export interface ImportedOptionGroup {
  id: string;
  nameEN: string;
  nameAR: string;
  minSelection?: number;
  maxSelection?: number;
  options: { id: string; nameEN: string; nameAR: string; extraPrice: number }[];
}

/** A single menu item within a submission. */
export interface SubmissionItem {
  id: string;
  name: string;
  /** Arabic name (from menu import or manual entry) */
  nameAR?: string;
  price: number;
  description?: string;
  /** Arabic description (from menu import or manual entry) */
  descriptionAR?: string;
  imageUrl?: string;
  /** Whether this item is locked (default true after initial submit) */
  locked?: boolean;
  /** Admin feedback note for this specific item */
  adminNote?: string;
  /** Timestamp when an admin unlocked this item for correction */
  unlockedAt?: number;
  /** Admin who unlocked this item */
  unlockedBy?: string;
  /** Timestamp when item was last resubmitted by Store Manager */
  resubmittedAt?: number;
  /** Item status — ACTIVE (default) or DISCARDED by Admin */
  itemStatus?: ItemStatus;
  /** Reason for discarding (Admin-provided) */
  discardReason?: string;
  /** Admin who discarded this item */
  discardedBy?: string;
  /** Timestamp when item was discarded */
  discardedAt?: number;
  // ── Menu Integration Fields ──
  /** True when this item was imported from Menu Management */
  menuLinked?: boolean;
  /** Source menu profile ID */
  menuProfileId?: string;
  /** Source menu profile name */
  menuProfileName?: string;
  /** Source menu item ID (for traceability) */
  menuItemId?: string;
  /** The original base price from the menu (for price-change highlighting) */
  originalMenuPrice?: number;
  /** Imported option groups / modifiers from the menu item */
  importedOptions?: ImportedOptionGroup[];
}

/** An approval/reject/unlock/resubmit action logged in the review history. */
export interface ReviewAction {
  departmentId: string;
  departmentName: string;
  action: 'APPROVE' | 'REJECT' | 'UNLOCK' | 'RESUBMIT' | 'DISCARD';
  reviewerId: string;
  reviewerName: string;
  note?: string;
  /** Per-item feedback — item IDs that were unlocked for correction */
  unlockedItemIds?: string[];
  /** Change details for RESUBMIT actions — human-readable list of what changed */
  changeDetails?: string[];
  timestamp: number;
}

/** A message in the departmental discussion thread. */
export interface DiscussionMessage {
  id: string;
  userId: string;
  userName: string;
  departmentName?: string;
  message: string;
  /** File attachment metadata */
  attachmentName?: string;
  attachmentType?: 'image' | 'document';
  /** Base64-encoded file data (persisted in LocalStorage) */
  attachmentData?: string;
  /** File size in bytes */
  attachmentSize?: number;
  /** @Mention references — department names tagged in this message */
  mentions?: string[];
  /** Optional reference to a specific submission item this message is about */
  itemRef?: { itemId: string; itemName: string };
  /**
   * Visibility flag:
   * - true  → visible to both Admin and Manager (public audit trail)
   * - false → visible to Admins only (internal war-room discussion)
   */
  public: boolean;
  timestamp: number;
}

/**
 * A campaign submission created by a Store Manager.
 * Flows through the department pipeline: F&B → F&P → SALES → DONE.
 */
export interface CampaignSubmission {
  id: string;
  campaignId: string;
  campaignName: string;
  brandId: string;
  brandName: string;
  /** User who submitted */
  submittedBy: string;
  submitterName: string;
  /** Current department stage for review — see DEPARTMENT_STAGES */
  currentStage: import('../../domain/entities').DepartmentStage;
  status: SubmissionStatus;
  items: SubmissionItem[];
  /** Full review history — every approve/reject/unlock action */
  reviewHistory: ReviewAction[];
  /** Departmental discussion thread */
  discussion?: DiscussionMessage[];
  /** Rejection reason (only when status === REJECTED) */
  rejectionReason?: string;
  /** ID of user who rejected */
  rejectedBy?: string;
  /** Timestamp of rejection */
  rejectedAt?: number;
  /** Postpone reason (only when status === POSTPONED) */
  postponeReason?: string;
  /** Optional target date to revisit the postponed launch */
  postponeUntil?: number;
  /** ID of user who postponed */
  postponedBy?: string;
  /** Timestamp when postponed */
  postponedAt?: number;
  /** Aggregator process tracker — persisted per-submission (campaign-level data shared across brands) */
  aggregatorProcess?: Record<string, { completed: boolean; completedAt?: number; completedBy?: string }>;
  /** Which aggregators this submission is currently live on */
  liveAggregators?: string[];
  createdAt: number;
  updatedAt: number;
  orgId?: string;
}

// ─── Rebranding Aliases (Aggregator Operations) ─────────────────────────────
// These aliases allow gradual adoption of the new naming convention.
export type AggregatorCampaign = Campaign;
export type AggregatorSubmission = CampaignSubmission;
export type AggregatorSettings = CampaignSettings;
